import { useEffect, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { navItems } from '@/lib/nav';
import { cn } from '@/lib/utils';
import { APP_VERSION } from '@/lib/version';

const ROTATE_MS = 20_000;
const REFRESH_MS = 60 * 60 * 1000;

export function TVShell({ children }: { children: React.ReactNode }) {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    document.body.classList.add('tv-mode');
    return () => {
      document.body.classList.remove('tv-mode');
    };
  }, []);

  // Auto-rotation onglets
  useEffect(() => {
    const id = setInterval(() => {
      const idx = navItems.findIndex((n) => n.to === location.pathname);
      const next = navItems[(idx + 1) % navItems.length];
      navigate({ to: next.to, search: { tv: '1' } as never });
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [location.pathname, navigate]);

  // Auto-refresh data
  useEffect(() => {
    const id = setInterval(() => {
      qc.invalidateQueries();
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [qc]);

  return (
    <div className="flex h-dvh w-dvw flex-col overflow-hidden bg-background text-foreground">
      <header className="flex h-12 items-center gap-3 border-b bg-background/90 px-6">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-black">M</span>
        <span className="text-lg font-bold tracking-wider">MAESTRO · WALLBOARD</span>
        <div className="ml-6 flex items-center gap-2">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            return (
              <span
                key={it.to}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors',
                  active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                )}
              >
                {it.label}
              </span>
            );
          })}
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          v{APP_VERSION} · auto-refresh 1h · auto-slide 20s · refreshs : {tick}
        </span>
      </header>
      <main className="flex-1 overflow-hidden p-4">{children}</main>
    </div>
  );
}
